/**
 * Impressão PDF/HTML do relatório único «Evolução clínica» (paciente + texto + impressos incorporados etc.).
 */

import axios from '../config/axios';
import { getApiUrl } from '../config/environment';
import { resolveEvolutionEntryFormat } from './clinicEvolutionFormat';
import { sanitizeClinicalHtml } from './clinicalHtml';
import { buildCertificatePrintHtml } from './certificatePrintDocument';
import { buildPrescriptionPrintHtml } from './prescriptionPrintDocument';
import type { EvolutionPrintableCategory } from '../components/evolution/evolutionWorkflowTypes';
import {
  formatEvolutionReturnWorkflowLines,
  getEvolutionReturnWorkflow,
  getEvolutionWorkflowPrintables,
  normalizeEvolutionProcedureLabels,
} from '../components/evolution/evolutionViewHelpers';
import { buildClinicPrintDocumentHtml, buildEvolutionPrintAttachmentSection, escapeHtml } from './clinicPrintLetterhead';

function openPrintHtmlWindow(html: string) {
  const w = window.open('', '_blank');
  if (w) {
    w.document.write(html);
    w.document.close();
    setTimeout(() => w.print(), 400);
  }
}

function extractBodyInner(fullHtml: string) {
  const m = fullHtml.match(/<body[^>]*>([\s\S]*)<\/body>/i);
  return m ? m[1].trim() : fullHtml;
}

/** Gera HTML completo já com timbre; quem recebe deve abrir e imprimir. */
export async function buildClinicalEvolutionFullPrintHtml(evo: any): Promise<string> {
  let clinic: any = null;
  try {
    const { data } = await axios.get(getApiUrl('/clinic/settings'));
    clinic = data?.data ?? null;
  } catch {
    /* ignore */
  }

  const vs = evo.vital_signs || {};
  const hasVitals = Object.values(vs).some((v) => v);
  const useFreeText = resolveEvolutionEntryFormat(evo) === 'free_text';

  const profSigSrc = String(evo?.professional?.signature_image_url ?? '').trim();
  const profSigImg = profSigSrc
    ? `<div class="prof-sig-print"><img src="${escapeHtml(profSigSrc)}" alt="" /></div>`
    : '';

  const nl2brClinical = (t: string) => sanitizeClinicalHtml(String(t || '').replace(/\r\n|\r|\n/g, '<br/>'));

  const evolutionFreePrintHtml =
    useFreeText && evo.evolution_free_text
      ? sanitizeClinicalHtml(String(evo.evolution_free_text).replace(/\r\n|\r|\n/g, '<br/>'))
      : '';

  const evolutionDateFormatted = evo.evolution_date
    ? new Date(evo.evolution_date + 'T12:00:00').toLocaleDateString('pt-BR')
    : '-';
  const timeShort = evo.evolution_time ? String(evo.evolution_time).slice(0, 5) : '';
  const subtitleBelowBadge = `Data: ${evolutionDateFormatted}${timeShort ? ` às ${timeShort}` : ''}`;

  const patientBirth =
    evo.patient?.birth_date ? new Date(evo.patient.birth_date + 'T12:00:00').toLocaleDateString('pt-BR') : '';

  const printableCatLab: Record<EvolutionPrintableCategory, string> = {
    prescription: 'Receituário',
    certificate: 'Atestado',
    exam_order: 'Exames',
    indication: 'Indicações',
    report: 'Laudo',
    surgical_risk: 'Risco cirúrgico',
  };

  const bodyTop = `
<h1 class="doc-title">Registro de atendimento</h1>
<div class="patient-info">
  <strong>Paciente:</strong> ${escapeHtml(evo.patient?.full_name || '-')}<br/>
  ${evo.patient?.cpf ? `<strong>CPF:</strong> ${escapeHtml(String(evo.patient.cpf))} &nbsp; ` : ''}
  ${patientBirth ? `<strong>Nascimento:</strong> ${escapeHtml(patientBirth)}` : ''}
</div>
${hasVitals ? `<div class="section">
  <div class="section-title">Sinais Vitais</div>
  <div class="vitals-grid">
    ${vs.blood_pressure_systolic ? `<div class="vital-item"><div class="vital-value">${vs.blood_pressure_systolic}/${vs.blood_pressure_diastolic || '-'}</div><div class="vital-label">PA (mmHg)</div></div>` : ''}
    ${vs.heart_rate ? `<div class="vital-item"><div class="vital-value">${vs.heart_rate}</div><div class="vital-label">FC (bpm)</div></div>` : ''}
    ${vs.temperature ? `<div class="vital-item"><div class="vital-value">${vs.temperature}°C</div><div class="vital-label">Temperatura</div></div>` : ''}
    ${vs.respiratory_rate ? `<div class="vital-item"><div class="vital-value">${vs.respiratory_rate}</div><div class="vital-label">FR (irpm)</div></div>` : ''}
    ${vs.oxygen_saturation ? `<div class="vital-item"><div class="vital-value">${vs.oxygen_saturation}%</div><div class="vital-label">SpO₂</div></div>` : ''}
    ${vs.weight ? `<div class="vital-item"><div class="vital-value">${vs.weight} kg</div><div class="vital-label">Peso</div></div>` : ''}
    ${vs.height ? `<div class="vital-item"><div class="vital-value">${vs.height} cm</div><div class="vital-label">Altura</div></div>` : ''}
    ${vs.bmi ? `<div class="vital-item"><div class="vital-value">${vs.bmi}</div><div class="vital-label">IMC</div></div>` : ''}
    ${vs.blood_glucose ? `<div class="vital-item"><div class="vital-value">${vs.blood_glucose}</div><div class="vital-label">Glicemia (mg/dL)</div></div>` : ''}
    ${vs.pain_scale !== undefined && vs.pain_scale !== null ? `<div class="vital-item"><div class="vital-value">${vs.pain_scale}/10</div><div class="vital-label">Dor</div></div>` : ''}
  </div>
</div>` : ''}
${evolutionFreePrintHtml ? `<div class="section"><div class="section-title">Evolução (texto livre)</div><div class="attachment-body">${evolutionFreePrintHtml}</div></div>` : ''}
${!useFreeText && evo.subjective ? `<div class="section"><div class="section-title"><span class="soap-label">S</span> Subjetivo</div><div class="attachment-body"><p>${nl2brClinical(evo.subjective)}</p></div></div>` : ''}
${!useFreeText && evo.objective ? `<div class="section"><div class="section-title"><span class="soap-label">O</span> Objetivo</div><div class="attachment-body"><p>${nl2brClinical(evo.objective)}</p></div></div>` : ''}
${!useFreeText && evo.assessment ? `<div class="section"><div class="section-title"><span class="soap-label">A</span> Avaliação</div><div class="attachment-body"><p>${nl2brClinical(evo.assessment)}</p></div></div>` : ''}
${!useFreeText && evo.plan ? `<div class="section"><div class="section-title"><span class="soap-label">P</span> Plano</div><div class="attachment-body"><p>${nl2brClinical(evo.plan)}</p></div></div>` : ''}
${evo.diagnosis ? `<div class="section"><div class="section-title">Diagnóstico</div><div class="attachment-body"><p>${nl2brClinical(evo.diagnosis)}${evo.diagnosis_code ? ` (CID: ${escapeHtml(String(evo.diagnosis_code))})` : ''}</p></div></div>` : ''}`;

  const procLabels = normalizeEvolutionProcedureLabels(evo);
  const procSection =
    procLabels.length > 0
      ? `<div class="section"><div class="section-title">Indicações</div><div class="attachment-body"><ul style="margin:0;padding-left:1.25rem">${procLabels.map((n) => `<li>${escapeHtml(n)}</li>`).join('')}</ul></div></div>`
      : '';

  const attachChunks: string[] = [];
  for (const p of getEvolutionWorkflowPrintables(evo)) {
    if (p.linkedDocumentId && p.linkedDocumentType === 'prescription') {
      try {
        const { data } = await axios.get(getApiUrl(`/clinic/prescriptions/${p.linkedDocumentId}/print`));
        const printData = data?.data ?? data;
        attachChunks.push(
          `<div class="attachment-block attachment-block--embedded">${extractBodyInner(buildPrescriptionPrintHtml(printData))}</div>`,
        );
      } catch {
        const body = sanitizeClinicalHtml(String(p.bodyText || '').replace(/\r\n|\r|\n/g, '<br/>'));
        const cat = printableCatLab[p.category] || p.category;
        attachChunks.push(buildEvolutionPrintAttachmentSection(cat, p.templateName, body));
      }
    } else if (p.linkedDocumentId && p.linkedDocumentType === 'certificate') {
      try {
        const { data } = await axios.get(getApiUrl(`/clinic/certificates/${p.linkedDocumentId}/print`));
        const printData = data?.data ?? data;
        attachChunks.push(
          `<div class="attachment-block attachment-block--embedded">${extractBodyInner(buildCertificatePrintHtml(printData))}</div>`,
        );
      } catch {
        const body = sanitizeClinicalHtml(String(p.bodyText || '').replace(/\r\n|\r|\n/g, '<br/>'));
        const cat = printableCatLab[p.category] || p.category;
        attachChunks.push(buildEvolutionPrintAttachmentSection(cat, p.templateName, body));
      }
    } else {
      const body = sanitizeClinicalHtml(String(p.bodyText || '').replace(/\r\n|\r|\n/g, '<br/>'));
      const cat = printableCatLab[p.category] || p.category;
      attachChunks.push(buildEvolutionPrintAttachmentSection(cat, p.templateName, body));
    }
  }
  const attachSection = attachChunks.join('');

  const wfLines = formatEvolutionReturnWorkflowLines(getEvolutionReturnWorkflow(evo));
  let returnInner = '';
  if (wfLines.length) {
    returnInner += wfLines.map((l) => `<p style="margin:0 0 0.35em 0">• ${escapeHtml(l)}</p>`).join('');
  }
  if (evo.follow_up_notes && String(evo.follow_up_notes).trim()) {
    returnInner += `<p style="margin:0.5em 0 0 0">${nl2brClinical(evo.follow_up_notes)}</p>`;
  }
  if (evo.follow_up_date) {
    returnInner += `<p style="margin:0.35em 0 0 0"><strong>Data de retorno:</strong> ${escapeHtml(new Date(evo.follow_up_date + 'T12:00:00').toLocaleDateString('pt-BR'))}</p>`;
  }
  const retSection =
    returnInner.trim() !== ''
      ? `<div class="section"><div class="section-title">Retorno</div><div class="attachment-body">${returnInner}</div></div>`
      : '';

  const footerBlock = `
<div class="sheet-evolution-footer">
  ${profSigImg}
  <div class="sheet-evolution-signature-rule"></div>
  <strong>${escapeHtml(evo.professional?.full_name || '-')}</strong><br/>
  ${escapeHtml(evo.professional?.specialty || '')}<br/>
  ${escapeHtml(`${evo.professional?.council_type || 'CRM'} ${evo.professional?.council_number || ''}${evo.professional?.council_state ? ` - ${evo.professional.council_state}` : ''}`)}
  ${evo.is_signed ? `<br/><small style="color:#22c55e;">✓ Assinado digitalmente em ${escapeHtml(evo.signed_at ? new Date(evo.signed_at).toLocaleString('pt-BR') : '')}</small>` : ''}
</div>`;

  const bodyInner = `${bodyTop}${procSection}${attachSection}${retSection}${footerBlock}`;

  return buildClinicPrintDocumentHtml({
    clinic,
    browserTitle: 'Evolução Clínica',
    badgeText: 'Evolução clínica',
    subtitleBelowBadge,
    bodyHtml: bodyInner,
    footerNote: 'Documento emitido pelo sistema da clínica.',
  });
}

export async function printClinicalEvolutionFullDocument(evo: any) {
  if (!evo) return;
  const html = await buildClinicalEvolutionFullPrintHtml(evo);
  openPrintHtmlWindow(html);
}
